"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.accessibilityGrantSchema = exports.zohoMigrationRecordSchema = exports.zohoFormCatalogSchema = exports.assetRequestSelectedSchema = exports.assetOnboardEmailLogSchema = exports.assetTransferTaskAnswerSchema = exports.onboardAssetTaskAnswerSchema = exports.updateAssetSettingTaskSchema = exports.assetSettingTaskSchema = exports.updateAssetSettingSchema = exports.assetSettingSchema = exports.assetLifecycleStageSchema = exports.servicePlanScheduleChecklistItemSchema = exports.serviceHistoryChecklistAnswerSchema = exports.dispatchJobProductSchema = exports.licenseRenewalSchema = exports.wofRenewalSchema = exports.apiCallLogSchema = exports.smsHistorySchema = exports.recycledAggregateSchema = exports.issueSchema = exports.catalogueItemSchema = exports.catalogueItemBaseSchema = exports.catalogueGroupSchema = exports.photoSchema = exports.timeLogSchema = exports.jobMaterialSchema = exports.materialSchema = exports.orderItemSchema = exports.orderSchema = exports.oldJobSheetSchema = exports.oldServiceHistoryReportSchema = exports.lastScheduledServiceInfoSchema = exports.checklistGroupSchema = exports.checkListSchema = exports.servicePlanScheduleChecklistSchema = exports.serviceHistoryDocumentSchema = exports.serviceMaterialCostSchema = exports.serviceLabourCostSchema = exports.employeeServiceRecordSchema = exports.upcomingServiceSchema = exports.trackerSettingsSchema = exports.runSheetScheduleSchema = exports.runSheetSchema = exports.assetRequestLogSchema = exports.rawAssetAuditSchema = exports.onboardAssetSchema = exports.assetTransferSchema = exports.vehicleCommentSchema = exports.vehicleFileSchema = void 0;
exports.pastServiceWindowSchema = exports.productSelectionSchema = exports.sectionHeaderSchema = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
const integrations_1 = require("./integrations");
// Minimal CRUD schemas for every new entity. Full-depth migration has all fields,
// but UI CRUD uses a simpler "edit the important fields" surface. Extend as needed.
exports.vehicleFileSchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: a file belongs to one asset
    fileName: common_1.strOrNull,
    fileUpload: common_1.strOrNull,
    fileKey: zod_1.z.string().min(1), // required: the actual S3 key
    processed: zod_1.z.boolean().optional(),
});
exports.vehicleCommentSchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: a comment belongs to one asset
    comment: zod_1.z.string().min(1), // required: empty comments are noise
});
exports.assetTransferSchema = zod_1.z.object({
    requestNumber: common_1.strOrNull,
    status: common_1.strOrNull,
    assetTransferred: zod_1.z.boolean().optional(),
    locked: zod_1.z.boolean().optional(),
    confirmFuelTag: zod_1.z.boolean().optional(),
    fleetListUpdated: zod_1.z.boolean().optional(),
    plantManagerNotified: zod_1.z.boolean().optional(),
    updateConcreteGo: zod_1.z.boolean().optional(),
    // `asset` is intentionally NOT required at the schema level — the
    // "+ Start transfer" toolbar action creates a stub `{status: 'Draft'}`
    // and routes the user into the wizard, where the built-in Assignment
    // stage's "Continue" button enforces asset before letting them advance.
    // Same shape as Bug #12 on onboardAssetSchema.fleet.
    asset: common_1.objectIdSchema.nullable().optional(),
    // The wizard persists current/new ref fields via PUT. Without these
    // listed here, Zod's default .strip() silently dropped them — wizard
    // saves were no-ops for all the assignment refs. (Caught by e2e wizard
    // test 2026-05-07.)
    currentCompany: common_1.objectIdSchema.nullable().optional(),
    currentSite: common_1.objectIdSchema.nullable().optional(),
    currentDepartment: common_1.objectIdSchema.nullable().optional(),
    currentLocation: common_1.objectIdSchema.nullable().optional(),
    currentVehicleGroup: common_1.objectIdSchema.nullable().optional(),
    currentServicePlan: common_1.objectIdSchema.nullable().optional(),
    newCompany: common_1.objectIdSchema.nullable().optional(),
    newSite: common_1.objectIdSchema.nullable().optional(),
    newDepartment: common_1.objectIdSchema.nullable().optional(),
    newLocation: common_1.objectIdSchema.nullable().optional(),
    newVehicleGroup: common_1.objectIdSchema.nullable().optional(),
    newServicePlan: common_1.objectIdSchema.nullable().optional(),
});
exports.onboardAssetSchema = zod_1.z.object({
    requestNumber: common_1.strOrNull,
    status: common_1.strOrNull,
    // `fleet` is intentionally NOT required at the schema level: the
    // "+ Start onboarding" toolbar action creates a stub OnboardAsset with
    // only `{status: 'Draft'}` and routes the user into the wizard, where
    // the built-in stage's "Continue" button enforces fleet before letting
    // them advance. The finalize endpoint also refuses to create the real
    // Asset without it. Requiring it here would block the legitimate stub-
    // creation flow.
    fleet: common_1.strOrNull,
    // Same caveat as assetTransferSchema — these are the BUILTIN_FIELDS the
    // onboard wizard persists, listed here so Zod's .strip() doesn't drop
    // them silently.
    purchaseOrderNo: common_1.strOrNull,
    registration: common_1.strOrNull,
    make: common_1.strOrNull,
    model: common_1.strOrNull,
    yearOfManufacture: common_1.numOrNull,
    colour: common_1.strOrNull,
    vin: common_1.strOrNull,
    serial: common_1.strOrNull,
    engine: common_1.strOrNull,
    comments: common_1.strOrNull,
});
exports.rawAssetAuditSchema = zod_1.z.object({
    date: common_1.dateLike,
    eventType: common_1.strOrNull,
    who: common_1.strOrNull,
    asset: common_1.objectIdSchema.nullable().optional(),
});
exports.assetRequestLogSchema = zod_1.z.object({
    requestType: common_1.strOrNull,
    requestStatus: common_1.strOrNull,
    description: common_1.strOrNull,
    fleet: common_1.strOrNull,
    rego: common_1.strOrNull,
});
exports.runSheetSchema = zod_1.z.object({
    branch: common_1.objectIdSchema, // required: every run sheet belongs to one branch
    date: zod_1.z.union([zod_1.z.string().datetime(), zod_1.z.string().min(1), zod_1.z.date()]), // required: every run sheet is for one day
    name: common_1.strOrNull,
    notes: common_1.strOrNull,
    status: common_1.strOrNull,
    assets: zod_1.z.array(common_1.objectIdSchema).optional(),
});
// Admin-configurable recurring run-sheet email — surfaced under
// Admin → Settings only, so non-admins never see the recipient list.
exports.runSheetScheduleSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    cron: zod_1.z.string().min(1).trim(),
    timezone: common_1.strOrNull,
    branch: common_1.objectIdSchema.nullable().optional(),
    vehicleGroups: zod_1.z.array(common_1.objectIdSchema).optional(),
    scope: zod_1.z.enum(['today', 'tomorrow', 'next-7-days']).optional(),
    recipients: zod_1.z.array(common_1.objectIdSchema).optional(),
    ccEmails: zod_1.z.array(zod_1.z.string().trim().email().or(zod_1.z.literal('')))
        .transform((arr) => arr.map((s) => s.trim()).filter((s) => s.length > 0))
        .optional(),
    emailSubject: common_1.strOrNull,
    emailMessage: common_1.strOrNull,
    active: zod_1.z.boolean().optional(),
    // Bookkeeping fields — admins shouldn't write these from the form, but
    // listed so the dispatcher's own model updates pass schema validation.
    lastRunAt: common_1.dateLike,
    lastRunStatus: zod_1.z.enum(['ok', 'failed']).nullable().optional(),
    lastRunError: common_1.strOrNull,
});
exports.trackerSettingsSchema = zod_1.z.object({
    primaryColour: common_1.strOrNull,
    defaultMapLocation: common_1.addressSchema,
});
exports.upcomingServiceSchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: upcoming service belongs to one asset
    servicePlanSchedule: common_1.objectIdSchema.nullable().optional(),
    nextServiceDate: common_1.dateLike,
    nextServiceValue: common_1.numOrNull,
    status: common_1.strOrNull,
});
exports.employeeServiceRecordSchema = zod_1.z.object({
    employee: common_1.objectIdSchema, // required: tracks one employee's stats
    numberOfServices: common_1.numOrNull,
    minuteSpent: common_1.numOrNull,
});
exports.serviceLabourCostSchema = zod_1.z.object({
    serviceHistory: common_1.objectIdSchema.nullable().optional(),
    employee: common_1.objectIdSchema.nullable().optional(),
    hours: common_1.numOrNull,
    cost: common_1.numOrNull,
});
exports.serviceMaterialCostSchema = zod_1.z.object({
    serviceHistory: common_1.objectIdSchema.nullable().optional(),
    description: common_1.strOrNull,
    cost: common_1.numOrNull,
});
exports.serviceHistoryDocumentSchema = zod_1.z.object({
    serviceHistory: common_1.objectIdSchema.nullable().optional(),
    documentName: common_1.strOrNull,
    fileUpload: common_1.strOrNull,
    fileKey: common_1.strOrNull,
    image: common_1.strOrNull,
    imageKey: common_1.strOrNull,
    type: common_1.strOrNull,
});
exports.servicePlanScheduleChecklistSchema = zod_1.z.object({
    servicePlan: common_1.objectIdSchema.nullable().optional(),
    servicePlanSchedule: common_1.objectIdSchema.nullable().optional(),
});
exports.checkListSchema = zod_1.z.object({
    listItem: zod_1.z.string().min(1),
    checklistGroup: common_1.objectIdSchema.nullable().optional(),
});
exports.checklistGroupSchema = zod_1.z.object({
    groupName: zod_1.z.string().min(1),
});
exports.lastScheduledServiceInfoSchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: this row tracks one asset's last service
    date: common_1.dateLike,
    odo: common_1.numOrNull,
});
exports.oldServiceHistoryReportSchema = zod_1.z.object({
    jobNo: common_1.strOrNull,
    jobDate: common_1.dateLike,
    fleetNo: common_1.strOrNull,
    rego: common_1.strOrNull,
    jobDesc: common_1.strOrNull,
});
exports.oldJobSheetSchema = zod_1.z.object({
    jobSheetNumber: common_1.strOrNull,
    date: common_1.dateLike,
    descriptionOfWorkDone: common_1.strOrNull,
    asset: common_1.objectIdSchema.nullable().optional(),
});
exports.orderSchema = zod_1.z.object({
    orderNumber: common_1.strOrNull,
    poNumber: common_1.strOrNull,
    dateOrdered: common_1.dateLike,
    status: common_1.strOrNull,
    total: common_1.numOrNull,
    customer: common_1.objectIdSchema, // required: every order has a customer
    branch: common_1.objectIdSchema.nullable().optional(),
});
exports.orderItemSchema = zod_1.z.object({
    order: common_1.objectIdSchema, // required: line item must belong to an order
    description: common_1.strOrNull,
    quantity: common_1.numOrNull,
    price: common_1.numOrNull,
    total: common_1.numOrNull,
});
exports.materialSchema = zod_1.z.object({
    materialName: zod_1.z.string().min(1),
    cost: common_1.numOrNull,
    qtyOnHand: common_1.numOrNull,
    // Listed explicitly so Zod's default .strip() doesn't drop the unit
    // picker value on save — the form submits it, the server was silently
    // discarding it, and the field appeared blank again after every edit.
    unit: common_1.objectIdSchema.nullable().optional(),
});
exports.jobMaterialSchema = zod_1.z.object({
    jobCard: common_1.objectIdSchema, // required: line item belongs to a job card
    material: common_1.objectIdSchema.nullable().optional(),
    // Listed explicitly so Zod's default .strip() doesn't drop the per-row
    // unit override on save (matches the materialSchema pattern above).
    unit: common_1.objectIdSchema.nullable().optional(),
    qty: common_1.numOrNull,
    cost: common_1.numOrNull,
});
exports.timeLogSchema = zod_1.z.object({
    employee: common_1.objectIdSchema, // required: a time log is one person's hours
    jobCard: common_1.objectIdSchema.nullable().optional(),
    logDate: zod_1.z.union([zod_1.z.string().datetime(), zod_1.z.string().min(1), zod_1.z.date()]), // required: must specify the day
    // Start/End drive the auto-computed `hours` column server-side (pre-save
    // hook on the TimeLog model). Operators bracket the work window; the
    // server does the maths. `hours` is intentionally not part of this schema
    // — any value the client tries to send for it will be rejected by the
    // factory's safeParse and never reach the doc.
    startTime: zod_1.z.union([zod_1.z.string().datetime(), zod_1.z.string().min(1), zod_1.z.date()]).nullable().optional(),
    endTime: zod_1.z.union([zod_1.z.string().datetime(), zod_1.z.string().min(1), zod_1.z.date()]).nullable().optional(),
    remarks: common_1.strOrNull,
}).superRefine((val, ctx) => {
    if (val.startTime == null || val.endTime == null)
        return;
    const start = new Date(val.startTime);
    const end = new Date(val.endTime);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()))
        return;
    if (end.getTime() <= start.getTime()) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['endTime'], message: 'End time must be after start time.' });
    }
});
exports.photoSchema = zod_1.z.object({
    takeAPhoto: common_1.strOrNull,
    imageKey: zod_1.z.string().min(1), // required: the photo file key
    type: common_1.strOrNull,
    jobSheet: common_1.objectIdSchema.nullable().optional(),
});
exports.catalogueGroupSchema = zod_1.z.object({
    name: zod_1.z.string().min(1),
    itemCatCode: common_1.strOrNull,
    description: common_1.strOrNull,
});
exports.catalogueItemBaseSchema = zod_1.z.object({
    itemCode: zod_1.z.string().min(1),
    // Excluded products are never pushed to Weightrax and need neither a JDE
    // code nor a Site.
    excludeFromWeightrax: zod_1.z.boolean().optional(),
    // JDE item number — the code Weightrax matches SaveJob's EXT_MATERIAL_CODE
    // against. From JDE (hand entry or CSV import). Not unique: several AFM
    // items can be one material (e.g. "GAP40" and "GAP 40" → GAP40); one of them
    // is the primary that is pushed, the rest are aliases (server-managed).
    jdeMaterialCode: common_1.jdeCodeSchema.nullable().optional(),
    // Sites whose Weightrax the material is pushed to; required unless excluded.
    sites: zod_1.z.array(common_1.objectIdSchema).optional(),
    description: common_1.strOrNull,
    price: common_1.numOrNull,
    imageKey: common_1.strOrNull,
    group: common_1.objectIdSchema.nullable().optional(),
    unit: common_1.strOrNull,
});
exports.catalogueItemSchema = exports.catalogueItemBaseSchema.superRefine((v, ctx) => {
    if (v.excludeFromWeightrax)
        return;
    if (!v.jdeMaterialCode) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['jdeMaterialCode'], message: 'Enter the JDE material code, or exclude the product from Weightrax' });
    }
    if (!v.sites?.length) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['sites'], message: 'Pick at least one site, or exclude the product from Weightrax' });
    }
});
exports.issueSchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: an issue is always against one asset
    description: zod_1.z.string().min(1), // required: the body of the issue
    priority: common_1.strOrNull,
    status: common_1.strOrNull,
});
exports.recycledAggregateSchema = zod_1.z.object({
    product: common_1.objectIdSchema.nullable().optional(),
    quantity: common_1.numOrNull,
    date: common_1.dateLike,
    notes: common_1.strOrNull,
});
exports.smsHistorySchema = zod_1.z.object({
    phoneNumber: common_1.strOrNull,
    message: common_1.strOrNull,
    deliveryStatus: common_1.strOrNull,
});
exports.apiCallLogSchema = zod_1.z.object({
    integrationName: common_1.strOrNull,
    status: common_1.strOrNull,
    errorMessage: common_1.strOrNull,
    executedAt: common_1.dateLike,
});
exports.wofRenewalSchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: WOF/COF tracks one asset
    wofCof: common_1.strOrNull,
    testingDate: common_1.dateLike,
    expiryDate: common_1.dateLike,
    processed: zod_1.z.boolean().optional(),
});
exports.licenseRenewalSchema = zod_1.z.object({
    asset: common_1.objectIdSchema, // required: license tracks one asset
    dateOfIssue: common_1.dateLike,
    licencePeriod: common_1.numOrNull,
    nztaReference: common_1.strOrNull,
    purchaseAmount: common_1.numOrNull,
    processed: zod_1.z.boolean().optional(),
});
exports.dispatchJobProductSchema = zod_1.z.object({
    dispatchJob: common_1.objectIdSchema, // required: line item belongs to a dispatch job
    catalogueItem: common_1.objectIdSchema, // required: pointing to a real catalogue item
    quantity: common_1.numOrNull,
    unit: common_1.strOrNull,
    status: zod_1.z.enum(integrations_1.DISPATCH_JOB_STATUSES).optional(), // omitted on create → model default 'Scheduled'
    netWeightKg: zod_1.z.number().nonnegative().nullable().optional(), // weighed; normally set by Weightrax via the integration API
});
exports.serviceHistoryChecklistAnswerSchema = zod_1.z.object({
    serviceHistory: common_1.objectIdSchema.nullable().optional(),
    jobCard: common_1.objectIdSchema.nullable().optional(),
    task: common_1.strOrNull,
    answer: common_1.strOrNull,
    comments: common_1.strOrNull,
    checkList: common_1.objectIdSchema.nullable().optional(),
});
exports.servicePlanScheduleChecklistItemSchema = zod_1.z.object({
    parent: common_1.objectIdSchema.nullable().optional(),
    checklistGroup: common_1.objectIdSchema.nullable().optional(),
    groupHeader: common_1.strOrNull,
    task: common_1.strOrNull,
    sortOrder: common_1.numOrNull,
    variant: zod_1.z.enum(['primary', 'alt']).optional(),
});
exports.assetLifecycleStageSchema = zod_1.z.object({
    kind: zod_1.z.enum(['onboard', 'transfer']),
    pointLink: common_1.strOrNull,
    pointDescription: common_1.strOrNull,
    stage: common_1.strOrNull,
    sourceField: common_1.strOrNull,
    assetField: common_1.strOrNull,
});
exports.assetSettingSchema = zod_1.z.object({
    kind: zod_1.z.enum(['onboard', 'transfer']),
    settingsName: common_1.strOrNull,
    apply: zod_1.z.boolean().optional(),
    selectAll: zod_1.z.boolean().optional(),
    clearAll: zod_1.z.boolean().optional(),
    stages: zod_1.z.array(zod_1.z.string()).optional(),
});
// Partial — used for PATCH-style updates (e.g. saving just the stages array).
exports.updateAssetSettingSchema = exports.assetSettingSchema.partial();
exports.assetSettingTaskSchema = zod_1.z.object({
    setting: common_1.objectIdSchema.nullable().optional(),
    kind: zod_1.z.enum(['onboard', 'transfer']),
    pointLink: common_1.strOrNull,
    pointName: common_1.strOrNull,
    pointDescription: common_1.strOrNull,
    stage: common_1.strOrNull,
    sortOrder: common_1.numOrNull,
    fieldType: zod_1.z.enum(['checkbox', 'text', 'textarea', 'number', 'date', 'select', 'ref', 'image']).optional(),
    fieldOptions: zod_1.z.array(zod_1.z.string()).optional(),
    refEndpoint: common_1.strOrNull,
    refLabel: common_1.strOrNull,
    required: zod_1.z.boolean().optional(),
    sourceField: common_1.strOrNull,
    assetField: common_1.strOrNull,
    decisionBox: zod_1.z.boolean().optional(),
    responsiblePerson: common_1.objectIdSchema.nullable().optional(),
    otherStaff: zod_1.z.array(common_1.objectIdSchema).optional(),
});
// Partial — used for PATCH-style updates from the task editor.
exports.updateAssetSettingTaskSchema = exports.assetSettingTaskSchema.partial();
exports.onboardAssetTaskAnswerSchema = zod_1.z.object({
    onboardAsset: common_1.objectIdSchema,
    task: common_1.objectIdSchema,
    value: zod_1.z.any().optional(),
    completedBy: common_1.objectIdSchema.nullable().optional(),
    completedAt: common_1.dateLike,
});
exports.assetTransferTaskAnswerSchema = zod_1.z.object({
    assetTransfer: common_1.objectIdSchema,
    task: common_1.objectIdSchema,
    value: zod_1.z.any().optional(),
    completedBy: common_1.objectIdSchema.nullable().optional(),
    completedAt: common_1.dateLike,
});
exports.assetOnboardEmailLogSchema = zod_1.z.object({
    assetOnboardRequest: common_1.objectIdSchema.nullable().optional(),
    emailSubject: common_1.strOrNull,
    message: common_1.strOrNull,
    recipients: common_1.strOrNull,
    replyTo: common_1.strOrNull,
});
exports.assetRequestSelectedSchema = zod_1.z.object({
    assetRequestLog: common_1.objectIdSchema.nullable().optional(),
    asset: common_1.objectIdSchema.nullable().optional(),
    assetIdText: common_1.strOrNull,
    fleet: common_1.strOrNull,
    markAs: common_1.strOrNull,
    reason: common_1.strOrNull,
});
exports.zohoFormCatalogSchema = zod_1.z.object({
    name: common_1.strOrNull,
    linkName: common_1.strOrNull,
    typeField: common_1.strOrNull,
});
exports.zohoMigrationRecordSchema = zod_1.z.object({
    newAssets: common_1.strOrNull,
    oldToNewAssetsUpdate: common_1.strOrNull,
});
exports.accessibilityGrantSchema = zod_1.z.object({
    target: zod_1.z.enum(['form', 'page', 'report']),
    role: common_1.objectIdSchema.nullable().optional(),
    form: common_1.objectIdSchema.nullable().optional(),
    sectionId: common_1.strOrNull,
    add: zod_1.z.boolean().optional(),
    view: zod_1.z.boolean().optional(),
    edit: zod_1.z.boolean().optional(),
    editOwnRecord: zod_1.z.boolean().optional(),
    delete: zod_1.z.boolean().optional(),
    customButton: zod_1.z.boolean().optional(),
});
exports.sectionHeaderSchema = zod_1.z.object({
    sectionId: common_1.strOrNull,
    sectionName: common_1.strOrNull,
});
exports.productSelectionSchema = zod_1.z.object({
    kind: zod_1.z.enum(['header', 'quantity']),
    hiddenProductList: common_1.strOrNull,
    itemId: common_1.strOrNull,
    catalogueItem: common_1.objectIdSchema.nullable().optional(),
    description: common_1.strOrNull,
    quantity: common_1.numOrNull,
});
// One time-window entry inside the "Log Past Service" submission. Each row
// becomes its own closed TimeLog server-side; the array as a whole belongs
// to a single past-service submission (one tech, one job card, one
// finalisation). Kept reusable so any future caller — mobile app, importer
// — gets the same row-level guarantees by composing this schema.
const pastServiceWindowEntrySchema = zod_1.z.object({
    startTime: zod_1.z.union([zod_1.z.string().datetime(), zod_1.z.string().min(1), zod_1.z.date()]),
    endTime: zod_1.z.union([zod_1.z.string().datetime(), zod_1.z.string().min(1), zod_1.z.date()]),
}).superRefine((val, ctx) => {
    const start = new Date(val.startTime);
    const end = new Date(val.endTime);
    if (!Number.isFinite(start.getTime())) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['startTime'], message: 'startTime must be a valid date.' });
        return;
    }
    if (!Number.isFinite(end.getTime())) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['endTime'], message: 'endTime must be a valid date.' });
        return;
    }
    if (start.getTime() >= end.getTime()) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['endTime'], message: 'Start time must be before end time.' });
    }
    if (end.getTime() > Date.now()) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['endTime'], message: 'End time cannot be in the future.' });
    }
});
// Body schema for POST /job-cards/:id/submit-past. A single submission may
// carry multiple time windows so a tech can record several stretches of
// work in one back-fill (e.g., 09:00–10:30 + 13:00–14:15 on the same day).
// All windows belong to the SAME service submission — they all become
// TimeLogs on this one JobCard.
exports.pastServiceWindowSchema = zod_1.z.object({
    windows: zod_1.z.array(pastServiceWindowEntrySchema).min(1, 'At least one time window is required.'),
});
//# sourceMappingURL=extras.js.map